import { type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../../../assets/digisaka-official-logo.png';
import pandoyLogo from '../../../assets/pandoy.png';
import welcomeHero from '../../../assets/welcome-hero.jpg';
import { useAuth } from '../../user-dashboard/auth/AuthContext';
import { AUTH_GREEN, AUTH_INK, AUTH_MUTED, AUTH_PAPER, AUTH_SCRIM } from '../../user-dashboard/auth/authTheme';

const CARD = {
  width: 402,
  cream: AUTH_PAPER,
  greenMid: AUTH_GREEN,
  textDark: AUTH_INK,
  textMuted: AUTH_MUTED,
} as const;

const FONT_SANS = "'Poppins', sans-serif";
const FONT_SERIF = "'Poppins', sans-serif";

function BrandMark() {
  return (
    <img
      src={logo}
      alt="Digisaka"
      className="absolute left-[22px] top-[22px] z-[2] h-10 w-auto sm:h-11"
      style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.35))' }}
    />
  );
}

function HeroPanel({
  headline,
  subheadline,
}: {
  headline: ReactNode;
  subheadline: ReactNode;
}) {
  return (
    <div className="relative aspect-[887/532] max-h-[min(52vh,520px)] min-h-[220px] w-full shrink-0 overflow-hidden sm:min-h-[300px]">
      <img
        src={welcomeHero}
        alt=""
        className="absolute inset-0 h-full w-full object-cover"
        style={{ objectPosition: '50% 32%' }}
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: [
            'linear-gradient(180deg, rgba(8,28,18,0.22) 0%, rgba(8,28,18,0.06) 32%, rgba(8,28,18,0.12) 58%, rgba(8,28,18,0.72) 100%)',
            'radial-gradient(85% 70% at 12% 8%, rgba(8,28,18,0.42) 0%, transparent 62%)',
            'radial-gradient(90% 55% at 50% 100%, rgba(8,28,18,0.35) 0%, transparent 70%)',
          ].join(', '),
        }}
        aria-hidden
      />
      <BrandMark />
      <div className="absolute bottom-5 left-[22px] right-[22px] z-[2] text-white">
        <h1
          id="welcome-access-heading"
          className="m-0 mb-2.5 text-[29px] font-bold leading-[1.16]"
          style={{ fontFamily: FONT_SERIF, textShadow: '0 2px 18px rgba(0,0,0,0.45)' }}
        >
          {headline}
        </h1>
        <p
          className="m-0 max-w-[88%] text-[12.5px] leading-[1.42]"
          style={{
            color: 'rgba(255,255,255,0.96)',
            textShadow: '0 1px 10px rgba(0,0,0,0.4)',
          }}
        >
          {subheadline}
        </p>
      </div>
    </div>
  );
}

function PrimaryButton({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-full border-none bg-[#008540] py-3.5 text-[14.5px] font-semibold text-white shadow-sm transition-[transform,background-color,box-shadow] duration-200 ease-out hover:-translate-y-px hover:bg-[#097a34] hover:shadow-md active:translate-y-0 active:scale-[0.98] motion-reduce:transform-none motion-reduce:transition-none"
      style={{ fontFamily: FONT_SANS }}
    >
      {children}
    </button>
  );
}

function SecondaryButton({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-2.5 flex w-full cursor-pointer items-center justify-center gap-2 rounded-full border-2 border-[#008540] bg-transparent py-3 text-[14px] font-semibold text-[#008540] transition-[transform,background-color,border-color,color,box-shadow] duration-200 ease-out hover:-translate-y-px hover:border-[#097a34] hover:bg-[#00854012] hover:text-[#097a34] hover:shadow-sm active:translate-y-0 active:scale-[0.98] motion-reduce:transform-none motion-reduce:transition-none"
      style={{ fontFamily: FONT_SANS }}
    >
      {children}
    </button>
  );
}

function WelcomeCardShell({ children }: { children: ReactNode }) {
  return (
    <div
      className="animate-auth-fade-up relative z-10 my-auto flex w-full max-w-[402px] shrink-0 flex-col overflow-x-hidden overflow-y-auto rounded-[28px] motion-reduce:animate-none"
      style={{
        background: CARD.cream,
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        animationDuration: '280ms',
        fontFamily: FONT_SANS,
        maxHeight: 'min(95vh, 900px)',
      }}
    >
      {children}
    </div>
  );
}

function CardBody({ children }: { children: ReactNode }) {
  return (
    <div
      className="flex shrink-0 flex-col items-center px-6 pb-6 pt-6 text-center"
      style={{ background: CARD.cream }}
    >
      {children}
    </div>
  );
}

/** Shown on the home map whenever there is no active session. */
export default function WelcomeAccessModal() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  if (authLoading || isAuthenticated) return null;

  const goAuth = () => navigate('/userdashboard');
  const goSignup = () => navigate('/userdashboard?view=signup');

  return (
    <div
      className="pointer-events-auto fixed inset-0 z-[200] flex items-center justify-center overflow-y-auto px-6 py-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-access-heading"
      data-digisaka-welcome-modal
    >
      <div
        className="absolute inset-0 backdrop-blur-[6px]"
        style={{ background: AUTH_SCRIM }}
        aria-hidden
      />

      <WelcomeCardShell>
        <HeroPanel
          headline={
            <>
              Your farm,
              <br />
              closer to you.
            </>
          }
          subheadline="Monitor and manage your farm from maps—even when you're away."
        />

        <CardBody>
          <p
            className="mb-4 mt-1 text-[13px] leading-[1.4]"
            style={{ color: CARD.textDark }}
          >
            Spot issues early. Plan your day. Make informed decisions.
          </p>

          <PrimaryButton onClick={goAuth}>
            Log in <span aria-hidden>&nbsp;→</span>
          </PrimaryButton>

          <SecondaryButton onClick={goSignup}>Create free account</SecondaryButton>

          <div className="mt-4 h-px w-full shrink-0" style={{ background: 'rgba(0,0,0,0.08)' }} />

          <div className="mt-4 flex w-full shrink-0 items-start gap-3 text-left">
            <img
              src={pandoyLogo}
              alt=""
              className="h-[34px] w-[34px] shrink-0 object-contain"
              aria-hidden
            />
            <div className="min-w-0 pb-0.5">
              <strong className="block text-[13px] leading-snug" style={{ color: CARD.textDark }}>
                Ask PandoyAI
              </strong>
              <span className="mt-1 block text-[11px] leading-[1.4]" style={{ color: CARD.textMuted }}>
                Need a hand? Understand your maps and find out what to do next.
              </span>
            </div>
          </div>
        </CardBody>
      </WelcomeCardShell>
    </div>
  );
}
